import {
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import S from './AssignAnnouncementAudioCardStyles';

export type AssignAnnouncementAudioCardProps = {
  id: string;
  name: string;
  durationSec?: number | null;
  sizeFormatted: string;
  audioUrl?: string | null;
  isSelected: boolean;
  onSelect: (id: string) => void;
  playingId?: string | null;
  onPlayPause?: (id: string, isPlaying: boolean) => void;
};

function PlayIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M3 2v10l8-5-8-5z" />
    </svg>
  );
}

function PauseIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <rect x="2" y="2" width="4" height="10" rx="1" />
      <rect x="8" y="2" width="4" height="10" rx="1" />
    </svg>
  );
}

export default function AssignAnnouncementAudioCard({
  id,
  name,
  durationSec,
  sizeFormatted,
  audioUrl,
  isSelected,
  onSelect,
  playingId,
  onPlayPause,
}: AssignAnnouncementAudioCardProps): JSX.Element {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleEnded = useCallback((): void => {
    setIsPlaying(false);
    onPlayPause?.(id, false);
  }, [id, onPlayPause]);

  useEffect(() => {
    setIsPlaying(false);
  }, [audioUrl]);

  useEffect(() => {
    if (playingId != null && playingId !== id && isPlaying) {
      if (audioUrl && audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [playingId, id, audioUrl, isPlaying]);

  const handlePlayPause = useCallback(
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      if (audioUrl && audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
          onPlayPause?.(id, false);
        } else {
          audioRef.current.play().catch(() => { });
          setIsPlaying(true);
          onPlayPause?.(id, true);
        }
        return;
      }
      const next = !isPlaying;
      setIsPlaying(next);
      onPlayPause?.(id, next);
    },
    [audioUrl, id, isPlaying, onPlayPause],
  );

  const handleCardClick = useCallback((): void => {
    onSelect(id);
  }, [id, onSelect]);

  const durationText = durationSec != null ? `${durationSec.toFixed(1)} sec` : '—';

  return (
    <>
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} onEnded={handleEnded} hidden>
          <track kind="captions" src="" label="English" />
        </audio>
      )}
      <S.Card
        type="button"
        $isSelected={isSelected}
        onClick={handleCardClick}
        aria-pressed={isSelected}
        aria-label={`Select ${name}`}
      >
        <S.Main>
          <S.TopRow>
            <S.PlayButton
              type="button"
              $isSelected={isSelected}
              onClick={handlePlayPause}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </S.PlayButton>
            <S.Content>
              <S.FileName title={name} $isSelected={isSelected}>{name}</S.FileName>
              <S.Meta $isSelected={isSelected}>
                {durationText} · {sizeFormatted}
              </S.Meta>
            </S.Content>
          </S.TopRow>
        </S.Main>
      </S.Card>
    </>
  );
}
